import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { replyEmail, forwardEmail, deleteMessage, moveMessage, flagMessage, markRead, categorizeMessage, getAttachments, getChildFolders } from '../api';
import Compose from './Compose';
import { colorByName } from '../categories';

function FolderPicker({ folders, onPick }) {
  const [stack, setStack] = useState([{ label: 'Folders', items: folders }]);
  const [search, setSearch] = useState('');
  const [children, setChildren] = useState({});

  const current = stack[stack.length - 1];

  async function drillInto(folder) {
    if (folder.childFolderCount > 0) {
      if (!children[folder.id]) {
        try {
          const data = await getChildFolders(folder.id);
          setChildren(prev => ({ ...prev, [folder.id]: data }));
          setStack(prev => [...prev, { label: folder.displayName, items: data, folderId: folder.id }]);
        } catch {}
      } else {
        setStack(prev => [...prev, { label: folder.displayName, items: children[folder.id], folderId: folder.id }]);
      }
      setSearch('');
    } else {
      onPick(folder.id);
    }
  }

  const filtered = current.items.filter(f =>
    f.displayName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <input
        className="dropdown-search"
        placeholder="Search…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      {stack.length > 1 && (
        <button className="folder-picker-back" onClick={() => { setStack(s => s.slice(0, -1)); setSearch(''); }}>
          ◂ {stack[stack.length - 2].label}
        </button>
      )}
      <div className="dropdown-items folder-picker-items">
        {filtered.map(f => (
          <div key={f.id} className="folder-picker-row">
            <span className="folder-pick-btn" onClick={() => onPick(f.id)}>Move here</span>
            <span className="folder-picker-name">{f.displayName}</span>
            {f.childFolderCount > 0
              ? <span className="folder-chevron-right" onClick={() => drillInto(f)}>▸</span>
              : <span className="folder-chevron-right-placeholder" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmailView({ message, folders, selectedFolderId, categoryMap, allCategories, pinnedIds = [], onTogglePin, signature, onDeleted, onMoved, onMessageUpdate, onRegisterUndo }) {
  const [mode, setMode] = useState(null);
  const [showMove, setShowMove] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode(null);
    setShowMove(false);
    setShowCategories(false);
    setAttachments([]);
    if (message?.hasAttachments) {
      getAttachments(message.id).then(setAttachments).catch(() => {});
    }
  }, [message?.id]);

  if (!message) {
    return (
      <div className="email-view-empty">
        <div className="empty-icon">✉</div>
        <div>Select a message to read</div>
      </div>
    );
  }

  const isFlagged = message.flag?.flagStatus === 'flagged';
  const isComplete = message.flag?.flagStatus === 'complete';
  const isPinned = pinnedIds.includes(message.id);

  async function handleDelete() {
    const origId = message.id;
    const origFolderId = selectedFolderId;
    try {
      const result = await deleteMessage(origId);
      const newId = result?.newId || origId;
      onDeleted(origId);
      onRegisterUndo?.('Email deleted', () => moveMessage(newId, origFolderId));
    } catch (e) { setError(e.message); }
  }

  async function handleMove(folderId) {
    const origId = message.id;
    const origFolderId = selectedFolderId;
    try {
      const result = await moveMessage(origId, folderId);
      const newId = result?.id || origId;
      onMoved(origId);
      setShowMove(false);
      onRegisterUndo?.('Email moved', () => moveMessage(newId, origFolderId));
    } catch (e) { setError(e.message); }
  }

  async function handleFlag() {
    const id = message.id;
    const prevStatus = message.flag?.flagStatus || 'notFlagged';
    const newStatus = isFlagged ? 'notFlagged' : 'flagged';
    try {
      await flagMessage(id, newStatus);
      onMessageUpdate(id, { flag: { flagStatus: newStatus } });
      onRegisterUndo?.(isFlagged ? 'Flag removed' : 'Email flagged', async () => {
        await flagMessage(id, prevStatus);
        onMessageUpdate(id, { flag: { flagStatus: prevStatus } });
      });
    } catch (e) { setError(e.message); }
  }

  async function handleComplete() {
    const id = message.id;
    const prevStatus = message.flag?.flagStatus || 'notFlagged';
    const newStatus = isComplete ? 'notFlagged' : 'complete';
    try {
      await flagMessage(id, newStatus);
      onMessageUpdate(id, { flag: { flagStatus: newStatus } });
      onRegisterUndo?.(isComplete ? 'Completed removed' : 'Marked complete', async () => {
        await flagMessage(id, prevStatus);
        onMessageUpdate(id, { flag: { flagStatus: prevStatus } });
      });
    } catch (e) { setError(e.message); }
  }

  async function handleMarkUnread() {
    const id = message.id;
    try {
      await markRead(id, false);
      onMessageUpdate(id, { isRead: false });
      onRegisterUndo?.('Marked unread', async () => {
        await markRead(id, true);
        onMessageUpdate(id, { isRead: true });
      });
    } catch (e) { setError(e.message); }
  }

  async function handleToggleCategory(catName) {
    const id = message.id;
    const prev = message.categories || [];
    const next = prev.includes(catName) ? prev.filter(c => c !== catName) : [...prev, catName];
    try {
      await categorizeMessage(id, next);
      onMessageUpdate(id, { categories: next });
      onRegisterUndo?.('Category changed', async () => {
        await categorizeMessage(id, prev);
        onMessageUpdate(id, { categories: prev });
      });
    } catch (e) { setError(e.message); }
  }

  const sender = message.from?.emailAddress;
  const initials = sender?.name
    ? sender.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
    : (sender?.address || '?')[0].toUpperCase();
  const hue = [...(sender?.name || sender?.address || '')].reduce((h, c) => h + c.charCodeAt(0), 0) % 360;

  return (
    <div className="email-view">
      {/* Toolbar */}
      <div className="email-view-toolbar">
        <button className="toolbar-btn" onClick={() => setMode('reply')} title="Reply">
          <span>↩</span> Reply
        </button>
        <button className="toolbar-btn" onClick={() => setMode('replyAll')} title="Reply All">
          <span>↩↩</span> Reply all
        </button>
        <button className="toolbar-btn" onClick={() => setMode('forward')} title="Forward">
          <span>↪</span> Forward
        </button>
        <div className="toolbar-divider" />
        <button className="toolbar-btn" onClick={handleDelete} title="Delete">🗑 Delete</button>
        <button className="toolbar-btn" onClick={() => setShowMove(v => !v)} title="Move to">
          📁 Move to {showMove ? '▴' : '▾'}
        </button>
        <button className="toolbar-btn" onClick={() => setShowCategories(v => !v)} title="Categorize">
          🏷 Categorize {showCategories ? '▴' : '▾'}
        </button>
        <div className="toolbar-divider" />
        <button className={`toolbar-btn ${isFlagged ? 'active-flag' : ''}`} onClick={handleFlag} title="Flag">
          🚩 {isFlagged ? 'Unflag' : 'Flag'}
        </button>
        <button className={`toolbar-btn ${isComplete ? 'active-complete' : ''}`} onClick={handleComplete} title="Mark complete">
          ✅ {isComplete ? 'Unmark' : 'Complete'}
        </button>
        <button className="toolbar-btn" onClick={handleMarkUnread} title="Mark as unread">
          📩 Unread
        </button>
        {onTogglePin && (
          <button className={`toolbar-btn ${isPinned ? 'active-flag' : ''}`} onClick={() => onTogglePin(message.id)} title={isPinned ? 'Unpin' : 'Pin'}>
            📌 {isPinned ? 'Unpin' : 'Pin'}
          </button>
        )}
        {message.importance === 'high' && (
          <span className="importance-badge high">! High importance</span>
        )}
      </div>

      {/* Dropdowns */}
      {showMove && (
        <div className="dropdown-panel">
          <div className="dropdown-title">Move to folder</div>
          <FolderPicker folders={folders} onPick={handleMove} />
        </div>
      )}

      {showCategories && (
        <div className="dropdown-panel">
          <div className="dropdown-title">Categorize</div>
          <div className="dropdown-items">
            {allCategories.map(cat => {
              const active = message.categories?.includes(cat.displayName);
              return (
                <button
                  key={cat.displayName}
                  className={active ? 'active-cat' : ''}
                  onClick={() => handleToggleCategory(cat.displayName)}
                >
                  <span className="cat-swatch" style={{ background: categoryMap?.[cat.displayName] || colorByName(cat.displayName) }} />
                  {cat.displayName}
                  {active && ' ✓'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="error-banner">{error} <button onClick={() => setError('')}>✕</button></div>}

      {/* Header */}
      <div className="email-view-header">
        <div className="email-view-avatar" style={{ background: `hsl(${hue},45%,48%)` }}>
          {initials}
        </div>
        <div className="email-view-meta">
          <div className="email-view-subject">{message.subject || '(no subject)'}</div>
          <div className="email-view-from">
            <strong>{sender?.name || sender?.address}</strong>
            {sender?.name && <span className="meta-email"> &lt;{sender.address}&gt;</span>}
          </div>
          <div className="email-view-details">
            <span>To: {message.toRecipients?.map(r => r.emailAddress.address).join(', ')}</span>
            {message.ccRecipients?.length > 0 && (
              <span> · Cc: {message.ccRecipients.map(r => r.emailAddress.address).join(', ')}</span>
            )}
            <span className="meta-date">{format(new Date(message.receivedDateTime), 'EEE, MMM d yyyy h:mm a')}</span>
          </div>
          {message.categories?.length > 0 && (
            <div className="email-view-cats">
              {message.categories.map(cat => (
                <span key={cat} className="category-chip" style={{ background: categoryMap?.[cat] || colorByName(cat) }}>{cat}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="attachment-bar">
          {attachments.map(a => (
            <div key={a.id} className="attachment-chip">
              <span className="attachment-icon">📎</span>
              <span className="attachment-name">{a.name}</span>
              <span className="attachment-size">({Math.round(a.size / 1024)}KB)</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div
        className="email-view-body"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.body?.content || message.bodyPreview || '') }}
      />

      {/* Inline compose */}
      {(mode === 'reply' || mode === 'replyAll' || mode === 'forward') && (
        <Compose
          mode={mode}
          originalMessage={message}
          signature={signature}
          onSend={async (payload) => {
            if (mode === 'forward') await forwardEmail(message.id, payload.to, payload.body);
            else await replyEmail(message.id, payload.body, mode === 'replyAll');
            setMode(null);
          }}
          onCancel={() => setMode(null)}
        />
      )}
    </div>
  );
}

function sanitizeHtml(html) {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}
